import { Field, Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import {
  FirmwareFileNameValidCharacters,
  fotaFileTypeOptions,
  getFileNameAndExtensionFromPath,
  MAX_FILE_SIZE,
} from "../../../constants/common-constants";
import { IOption } from "../../../models/ICommon";
import { IAddFotaHostConfig } from "../../../models/response/IFotaNewResponse";
import LoaderButton from "../../../shared-components/Button/LoaderButton";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormFileSizeInputField from "../../../shared-components/FormikFormHandlers/FormikFormFileSizeInputField";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  addValidationSchemaTenantAdmin,
  updateValidationSchemaTenantAdmin,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import Label from "../../../shared-components/Label/Label";
import CustomTooltip from "../../../shared-components/Tooltip/tooltip";
import { formatMessage } from "../../../translations/format-message";
import "./fota-form.css";

export interface IProps {
  id: number;
  initialValues: IAddFotaHostConfig;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  dataLoading: boolean;
  actionLoading: boolean;
  groupOptions: IOption[];
}

const AddEditFotaTenantForm = (props: IProps) => {
  const [hostVersionFileContent, setHostVersionFileContent] = useState<any>("");
  const [selectedFileType, setSelectedFileType] = useState<any>(
    props.initialValues.FileType
  );

  const handleFileReadHostVersionFileAscii = (content: any) => {
    setHostVersionFileContent(content);
  };

  const handleFileReadHostVersionFileHex = (event: any) => {
    const content = event.target.result;
    setHostVersionFileContent(content);
  };

  const FormObserver: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<IAddFotaHostConfig>();

    useEffect(() => {
      if (
        props.id > 0 &&
        JSON.stringify(values) !== JSON.stringify(props.initialValues)
      ) {
        if (values.WantToUpdateFile === "false") {
          setFieldValue("File", "");
          setFieldValue("FileName", "");
        }
      }
    }, [values.WantToUpdateFile]);

    useEffect(() => {
      if (selectedFileType != values.FileType) {
        setFieldValue("File", "");
        setSelectedFileType(values.FileType);
      }
    }, [values.FileType]);

    return null;
  };

  return (
    <Modal
      className="fancy-modal"
      show={true}
      size="lg"
      centered
      onHide={() => { }}
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage
            id={props.id > 0 ? "edit_host_config" : "add_host_config"}
          />
        </Modal.Title>
        <button
          onClick={(e) => props.onModalClosed()}
          disabled={props.actionLoading}
          className="Crossicons"
          title={formatMessage("close")}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23"
            height="23"
            fill="currentColor"
            className="bi bi-x"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={props.initialValues}
          validationSchema={
            props.id >= 0
              ? updateValidationSchemaTenantAdmin
              : addValidationSchemaTenantAdmin
          }
          enableReinitialize
          validateOnBlur={false}
          onSubmit={async (values: any) => {
            let fileDetails = getFileNameAndExtensionFromPath(values.File);
            let tempValues: IAddFotaHostConfig = {
              ...values,
              FileName:
                props.id === -1 ? (fileDetails.Name + fileDetails.Extension) :
                  (props.id > 0 && hostVersionFileContent == ""
                    ? props.initialValues?.FileName
                    : fileDetails.Name + fileDetails.Extension),
              File:
                props.id > 0
                  ? values.WantToUpdateFile === "true"
                    ? hostVersionFileContent
                    : ""
                  : hostVersionFileContent,
            };
            if (tempValues.File?.length <= MAX_FILE_SIZE)
              props.submitHandler({ ...tempValues });
          }}
        >
          {({
            setErrors,
            values,
            errors,
            validateForm,
            handleSubmit,
            setFieldValue,
          }) => {
            return (
              <Form className="user-form" onSubmit={handleSubmit}>
                <FormObserver />
                {props.dataLoading || props.actionLoading ? (
                  <FormLoader
                    loading={props.dataLoading || props.actionLoading}
                  />
                ) : null}
                <>
                  <div className="">
                    <div className="row">
                      <div className="form-group col-md-4">
                        <Label
                          className="label-style col-form-label"
                          required={true}
                          label="fota_config_name"
                        />
                        <CustomTooltip
                          id="custom_name_info"
                          message="name_validation"
                        />
                        <Field
                          data-testid="ConfigName"
                          className="form-control"
                          name="ConfigName"
                          validcharacter={["@", "#", "$", "-", "_", "."]}
                          placeholder="fota_config_name"
                          type="text"
                          as={FormikFormInput}
                        />
                      </div>
                      <div className="form-group col-md-4">
                        <Label
                          className="label-style col-form-label"
                          required={true}
                          label="version"
                        />
                        <Field
                          className="form-control"
                          name="Version"
                          invalidcharacter={[" "]}
                          validcharacter={[".", "-", "_"]}
                          type="text"
                          placeholder="version"
                          as={FormikFormInput}
                        />
                      </div>
                      <div className="form-group col-md-4">
                        <Label
                          className="label-style col-form-label"
                          required={true}
                          label="sending_method"
                        />
                        <Field
                          className="form-control"
                          name="FileType"
                          placeholder="sending_method"
                          options={fotaFileTypeOptions}
                          as={FormikFormSelect}
                        />
                      </div>
                      {props.id > 0 && props.initialValues.FileContent && (
                        <div className="alert alert-warning">
                          <FormattedMessage id={"file_upload_message_fota"} />
                        </div>
                      )}
                      {props.id > 0 && (
                        <div className="form-group row d-flex align-items-center">
                          <div className="col-md-4">
                            <Field
                              type="radio"
                              name="WantToUpdateFile"
                              value="false"
                              id="useOldFile"
                              className="form-check-input"
                            />
                            <label
                              className="form-check-label"
                              htmlFor="useOldFile"
                            >
                              <FormattedMessage id="use_old_file" />
                            </label>
                          </div>
                          <div className="col-md-8">
                            <Field
                              type="radio"
                              name="WantToUpdateFile"
                              value="true"
                              id="WantToUpdateFile"
                              className="form-check-input"
                            />
                            <label
                              className="form-check-label"
                              htmlFor="WantToUpdateFile"
                            >
                              <FormattedMessage id="provide_new_file" />
                            </label>
                          </div>
                        </div>
                      )}
                      {((props.id > 0 && values.WantToUpdateFile === "true") ||
                        props.id === -1) && (
                          <>
                            <div className="form-group col-md-12">
                              <Label
                                className="label-style me-2 col-form-label"
                                required={true}
                                label="firmware_file"
                              />
                              <CustomTooltip
                                id="custom_name_info"
                                message="file_size_validation"
                              />
                              <Field
                                className="form-control"
                                name="File"
                                showfilesizeerror="true"
                                filesize={MAX_FILE_SIZE}
                                selectedFileType={values.FileType}
                                handleFileUploadHex={
                                  handleFileReadHostVersionFileHex
                                }
                                handleFileUploadAscii={
                                  handleFileReadHostVersionFileAscii
                                }
                                as={FormikFormFileSizeInputField}
                              />
                            </div>
                            {/* <div className="form-group col-md-12 mt-1">
                            <div className="row">
                              <div className="col-md-2">
                                <label className="">
                                  <FormattedMessage id="extension" />
                                </label>
                                <CustomTooltip
                                  id="custom_file_name_info"
                                  message="custom_file_name_info"
                                />
                              </div>
                              <div className="col-md-5 form-check">
                                <Field
                                  data-testid="IsCustomFileNameWithExtension"
                                  className="form-check-input"
                                  name="IsCustomFileNameWithExtension"
                                  type="checkbox"
                                  id="IsCustomFileNameWithExtension"
                                  checked={values.IsCustomFileNameWithExtension}
                                  onChange={handleCheck}
                                />
                                <label
                                  htmlFor="IsCustomFileNameWithExtension"
                                  className="form-check-label"
                                >
                                  <FormattedMessage id="with_extension" />
                                </label>
                              </div>
                              <div className="col-md-5 form-check">
                                <Field
                                  data-testid="IsCustomFileNameWithoutExtension"
                                  className="form-check-input"
                                  name="IsCustomFileNameWithoutExtension"
                                  type="checkbox"
                                  id="IsCustomFileNameWithoutExtension"
                                  checked={
                                    values.IsCustomFileNameWithoutExtension
                                  }
                                  onChange={handleCheck}
                                />
                                <label
                                  htmlFor="IsCustomFileNameWithoutExtension"
                                  className="form-check-label"
                                >
                                  <FormattedMessage id="without_extension" />
                                </label>
                              </div>
                            </div>
                            <Field
                              disabled={
                                !(
                                  values.IsCustomFileNameWithExtension ||
                                  values.IsCustomFileNameWithoutExtension
                                )
                              }
                              className="form-control mt-2"
                              name="FileName"
                              validcharacter={FirmwareFileNameValidCharacters}
                              type="text"
                              placeholder="file_name"
                              as={FormikFormInput}
                            />
                          </div> */}
                          </>
                        )}
                    </div>
                  </div>
                </>
                <div className="form-group">
                  <Label
                    className="label-style col-form-label"
                    label="fota_desc"
                  />
                  <Field
                    className="form-control"
                    name="Description"
                    type="text"
                    validcharacter={["."]}
                    placeholder="fota_desc"
                    as={FormikFormTextArea}
                  />
                </div>
                <div className="form-group row g-0 tab-action text-end mt-4">
                  <div className="col-12">
                    <LoaderButton
                      type="submit"
                      isLoading={props.actionLoading}
                      text={props.id > 0 ? "update" : "add"}
                    />
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={(e) => props.onModalClosed()}
                      disabled={props.actionLoading}
                    >
                      <FormattedMessage id="button_cancel" />
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditFotaTenantForm;
